import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, Zap, User, LogOut, FileText } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useAccount } from "../../lib/account";
import { ASSETS } from "../../lib/data";
import { AccountModal } from "./AccountModal";
import { MyQuotesModal } from "./MyQuotesModal";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { t, toggle, lang } = useLang();
  const { user, logout } = useAccount();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [quotesOpen, setQuotesOpen] = useState(false);
  const firstName = user?.name?.split(" ")[0] || "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "product", label: t.nav.product },
    { id: "story", label: t.nav.story },
    { id: "why", label: t.nav.why },
    { id: "calculator", label: t.nav.calculator },
    { id: "stories", label: t.nav.stories },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 h-[72px]">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-2 group" data-testid="logo-btn">
          <img src={ASSETS.logo} alt="Gesung" className="h-8 w-8 rounded-md object-cover" />
          <span className="font-display font-extrabold text-lg tracking-tight text-offwhite">
            GESUNG
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-${l.id}`}
              className="text-sm text-white/70 hover:text-lime2 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="text-xs font-semibold border border-white/20 rounded-full px-3 py-1.5 text-white/80 hover:border-lime2 hover:text-lime2 transition-colors duration-300"
          >
            {lang === "en" ? "EN / FR" : "FR / EN"}
          </button>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => setQuotesOpen(true)}
                data-testid="nav-account-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold border border-white/20 rounded-full px-4 py-2 text-white/85 hover:border-lime2 hover:text-lime2 transition-colors duration-300"
              >
                <User size={15} /> {t.account.greeting}, {firstName}
              </button>
              <button
                onClick={logout}
                data-testid="nav-logout-btn"
                aria-label={t.account.signOut}
                className="text-white/60 hover:text-lime2 transition-colors p-2"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAuthOpen(true)}
              data-testid="nav-signin-btn"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold border border-white/20 rounded-full px-4 py-2 text-white/85 hover:border-lime2 hover:text-lime2 transition-colors duration-300"
            >
              <User size={15} /> {t.account.signIn}
            </button>
          )}

          <button
            onClick={() => scrollTo("contact")}
            data-testid="nav-quote-btn"
            className="hidden sm:inline-flex items-center gap-1.5 bg-emerald2 hover:bg-lime2 hover:text-black text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors duration-300"
          >
            <Zap size={15} /> {t.nav.contact}
          </button>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-xl border-t border-white/10"
          >
            <div className="flex flex-col p-5 gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left text-white/80 py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { scrollTo("contact"); setOpen(false); }}
                className="bg-emerald2 text-white rounded-full px-5 py-2.5 font-semibold"
              >
                {t.nav.contact}
              </button>
              {user ? (
                <>
                  <button
                    onClick={() => { setQuotesOpen(true); setOpen(false); }}
                    data-testid="mobile-account-btn"
                    className="flex items-center gap-2 text-left text-white/85 py-1"
                  >
                    <FileText size={16} /> {t.account.myQuotes}
                  </button>
                  <button
                    onClick={() => { logout(); setOpen(false); }}
                    data-testid="mobile-logout-btn"
                    className="flex items-center gap-2 text-left text-white/60 py-1"
                  >
                    <LogOut size={16} /> {t.account.signOut}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setAuthOpen(true); setOpen(false); }}
                  data-testid="mobile-signin-btn"
                  className="flex items-center gap-2 text-left text-white/85 py-1"
                >
                  <User size={16} /> {t.account.signIn}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AccountModal open={authOpen} onOpenChange={setAuthOpen} />
      <MyQuotesModal open={quotesOpen} onOpenChange={setQuotesOpen} />
    </motion.header>
  );
};
